import { momentToDayjs, type DatetimeType } from '@common/services/datetime';
import type { Moment } from 'moment';

import { type MomentOpType } from './metrics';
import {
  classifyOp,
  DAYJS_METHOD_ALIAS,
  type OpClassType,
} from './op-allowlist';
import { compareAndEmit } from './shadow-runner';
import type { DomainNameType } from './subdomains';

// The chainable shadow proxy (M3.4, WORK-6305). M3.1 compares only the four
// construction entry points and returns a raw Moment, so every chained op
// (`.add`, `.diff`, `.startOf`, setters, …) runs Moment-only. This proxy carries
// the parallel dayjs instance alongside the Moment and runs the same comparison
// on every instrumented hop, while remaining a transparent Moment to consumers
// (`moment.isMoment(proxy) === true`, no call-site changes).

type PairType = { m: Moment; d: DatetimeType };
type TagsType = { domain: DomainNameType; subdomain: string };

// Private channel for reading a proxy's carried dayjs instance during arg
// conversion. A Symbol can't collide with any Moment property, and the get trap
// answers it before delegating to the target — so a proxy handed as an argument
// to `.diff`/`.isBefore`/… resolves to its dayjs peer rather than being run back
// through `momentToDayjs` (which would re-parse a value we already have).
const SHADOW_D = Symbol('momentShadowDayjs');

const THREW = '<threw>';

function safeFormat(value: { format: () => string } | undefined): string {
  if (value === undefined) return THREW;
  try {
    return value.format();
  } catch {
    return THREW;
  }
}

// Resolve a comparison argument to the value the dayjs side should receive:
//   - a shadow proxy        → its carried dayjs instance (no re-parse);
//   - a raw Moment          → momentToDayjs (offset-preserving .format() round-trip
//                             via M0.2 — never .toDate(), which would drop the zone);
//   - a primitive / token   → passed through unchanged (units like 'hours', counts).
function toDayjsArg(arg: unknown): unknown {
  if (arg !== null && typeof arg === 'object') {
    const carried = (arg as Record<symbol, unknown>)[SHADOW_D];
    if (carried !== undefined) {
      return carried;
    }
    if ((arg as { _isAMomentObject?: boolean })._isAMomentObject === true) {
      return momentToDayjs(arg as Moment);
    }
  }
  return arg;
}

export function wrapShadowMoment(pair: PairType, tags: TagsType): Moment {
  return new Proxy(pair.m, {
    get(target, prop, receiver) {
      if (prop === SHADOW_D) {
        return pair.d;
      }

      const value = Reflect.get(target, prop, receiver);
      // Non-functions (`_isAMomentObject`, `_d`, Symbol.toPrimitive, …) and the
      // type-guard markers `moment.isMoment` reads pass straight through, so the
      // proxy is indistinguishable from the underlying Moment.
      if (typeof value !== 'function') {
        return value;
      }

      const op = typeof prop === 'string' ? prop : '';

      return (...args: unknown[]): unknown => {
        const klass: OpClassType = op
          ? classifyOp(op, args.length)
          : 'pass-through';

        // Delegate uninstrumented ops to the real Moment. Bind to `target` (never
        // the proxy) so internal `this` access can't recurse back through the trap.
        if (klass === 'pass-through') {
          return value.apply(target, args);
        }

        // Moment mutates in place and returns the same reference; we keep that
        // reference (never clone it) so consumer-visible mutation semantics hold.
        const mNext = value.apply(target, args);

        const dMethod = DAYJS_METHOD_ALIAS[op] ?? op;
        const dFn = (pair.d as unknown as Record<string, unknown>)[dMethod];
        const dCallable = typeof dFn === 'function' ? dFn : undefined;

        // `clone` (and any rewrap op) carries no meaningful comparison — emitting a
        // counter for an identity op would be pure noise — but it MUST re-wrap so
        // the chain after `.clone()` stays shadowed.
        if (klass === 'rewrap') {
          const dNext = dCallable
            ? (dCallable.apply(pair.d, args) as DatetimeType)
            : pair.d;
          return wrapShadowMoment({ m: mNext as Moment, d: dNext }, tags);
        }

        // Drive the dayjs side. A missing peer (no dayjs equivalent) is treated as
        // a throw: it short-circuits the comparison rather than inventing a result.
        let dNext: unknown;
        let dThrew = false;
        if (dCallable) {
          try {
            dNext = dCallable.apply(pair.d, args.map(toDayjsArg));
          } catch {
            dThrew = true;
          }
        } else {
          dThrew = true;
        }

        if (klass === 'terminal') {
          // Scalar / string / boolean output — compare serialized, return the raw
          // Moment scalar. normalizeForComparison (inside compareAndEmit) absorbs
          // the benign Invalid date casing divergence.
          compareAndEmit({
            op: op as MomentOpType,
            ...tags,
            method: op,
            inputs: args,
            momentSerialized: String(mNext),
            dayjsSerialized: dThrew ? THREW : String(dNext),
            dayjsThrew: dThrew,
          });
          return mNext;
        }

        // Chaining op — compare the produced instant via `.format()`, then return a
        // re-wrapped pair so the next hop is shadowed. mNext is the same (mutated)
        // Moment; dNext is the fresh dayjs value.
        compareAndEmit({
          op: op as MomentOpType,
          ...tags,
          method: op,
          inputs: args,
          momentSerialized: safeFormat(mNext as { format: () => string }),
          dayjsSerialized: dThrew
            ? THREW
            : safeFormat(dNext as { format: () => string }),
          dayjsThrew: dThrew,
        });

        // Moment chaining ops (`add`, `.utc(true)`, `.date(1)`, …) MUTATE the
        // instance in place and return the same reference; dayjs is immutable and
        // returns a fresh value. If we carried the fresh dayjs only on the NEW proxy
        // and left this pair's `d` untouched, the ORIGINAL reference — whose Moment
        // was just mutated under it — would keep its stale (pre-op) dayjs. A consumer
        // that holds that original reference and keeps using it then compares a
        // mutated Moment against an un-mutated dayjs: AntD's <DatePicker> does exactly
        // this — `onChange={(v) => v.utc(true)}` mutates its value in place (the
        // returned value is discarded) and rc-picker re-feeds the SAME object through
        // `.add(0,"day")` / `.date(1)` / `.format(…)`, surfacing as WORKS-33D/E/F.
        // Mirror Moment's in-place semantics: when the Moment mutated in place,
        // advance THIS pair's dayjs too and re-wrap the same pair, so every proxy over
        // this Moment stays in sync. (dThrew → no peer to advance; keep the stale d so
        // the next comparison still short-circuits as a throw rather than inventing a
        // value.)
        if (!dThrew && mNext === target) {
          pair.d = dNext as DatetimeType;
          return wrapShadowMoment(pair, tags);
        }
        return wrapShadowMoment(
          { m: mNext as Moment, d: dNext as DatetimeType },
          tags
        );
      };
    },
  }) as Moment;
}
